/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { employeesAPI, walletAPI, assetCheckoutAPI, evaluationsAPI, employeeAttachmentsAPI, locationsAPI, employeeLicensesAPI, employeeEmergencyContactsAPI, lookupsAPI } from '../lib/api';
import { settle } from '../lib/useResilientLoad';
import { useEffectivePermissions } from '../hooks/useEffectivePermissions';
import AccessState from '../components/auth/AccessState';
import PayrollTab from './employee-tabs/PayrollTab';
import ConfidentialTab from './employee-tabs/ConfidentialTab';
import EmployeeHistoryTab from '../components/employee/EmployeeHistoryTab';
import { toast } from 'sonner';
import RequireCap from '../components/auth/RequireCap';
import SensitiveField from '../components/auth/SensitiveField';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import { ArrowLeft, Save, Package, Trash2, Laptop, MapPin, FileText, Upload, Download, ClipboardCheck, Plus, ClipboardList, FolderOpen, UserCircle, Badge as BadgeIcon, Banknote, ShieldCheck, Construction, History } from 'lucide-react';
import { formatDate } from '../lib/utils';
import { US_STATES } from '../lib/usStates';
import { SearchableCombobox } from '../components/SearchableCombobox';
import { AttachmentsManager } from '../components/AttachmentsManager';
import { SupervisorLogPanel } from '../components/SupervisorLogPanel';
import { LicensingPanel } from '../components/LicensingPanel';
import { FirearmsQualificationPanel } from '../components/FirearmsQualificationPanel';
import { EmergencyContactsPanel } from '../components/EmergencyContactsPanel';
import { useAuth } from '../context/AuthContext';

// ── SSN helpers (used on new-employee create form) ────────────────────
// Format-as-you-type: keep digits only, insert dashes after 3 and 5.
const formatSsn = (raw) => {
  const d = (raw || '').replace(/\D+/g, '').slice(0, 9);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
};
// Returns null when input is valid (or empty), otherwise a human-readable reason.
// Matches the Social Security Administration's structural rules — not a true Luhn
// (SSNs don't use Luhn), but rules out the common bad patterns.
const validateSsn = (raw) => {
  const d = (raw || '').replace(/\D+/g, '');
  if (d.length === 0) return null; // blank is allowed — operator can add later
  if (d.length !== 9) return 'SSN must be exactly 9 digits';
  const area = d.slice(0, 3);
  const group = d.slice(3, 5);
  const serial = d.slice(5);
  if (area === '000' || area === '666' || area.startsWith('9')) return 'Invalid area number';
  if (group === '00') return 'Invalid group number';
  if (serial === '0000') return 'Invalid serial number';
  if (/^(\d)\1{8}$/.test(d)) return 'SSN cannot be all the same digit';
  return null;
};

export default function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const perms = useEffectivePermissions();
  const isNew = id === 'new';
  // Capability, not role list (2026-09-08): `supervisor` and the GSOC
  // supervisory roles hold employees:view and are exactly who keeps a
  // supervisor log. The API is span-of-control gated either way.
  const canSeeSupervisorLog = perms.can('employees', 'view');

  // ── Phase 4 — Tab visibility driven by the access engine ──────────────
  // The Payroll sub-tabs (Compensation, W-4, State Withholding, Banking)
  // require the `payroll` HR sub-area grant. The Confidential sub-tabs
  // (EEO, I-9, WOTC, Sensitive) require the `confidential` HR grant.
  // Users without the grant never see the tab — we don't fire the request
  // either. See useEffectivePermissions hook and access_engine.HRGrant.
  const canSeePayrollTab = perms.hasGrant ? perms.hasGrant('payroll') : false;
  const canSeeConfidentialTab = perms.hasGrant ? perms.hasGrant('confidential') : false;
  // Executive-comp records are walled even from general payroll holders.
  // Used inside the Payroll tab (Sub-Pass 2) to decide whether to reveal
  // is_executive=true rows.
  // const canSeeExecutiveComp = perms.hasGrant ? perms.hasGrant('executive_compensation') : false;
  
  const [loading, setLoading] = useState(!isNew);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'details');
  const [saving, setSaving] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [itCheckouts, setItCheckouts] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [employee, setEmployee] = useState({
    employee_number: '',
    first_name: '',
    last_name: '',
    email: '',
    // Defaults (2026-06-26): Company defaults to "Security" per spec; the
    // operator can change to Cyber / Exquisite / Surveillance / Other.
    company: 'Security',
    position: '',
    assignment_location: '',
    location_id: '',
    hire_date: '',
    post_type: '',
    state: '',
    client_ids: [],
    middle_name: '',
    address: '',
    city: '',
    zip_code: '',
    phone_primary: '',
    phone_secondary: '',
    date_of_birth: '',
    classification_description: '',
    // Pay Distribution defaults to ACH (the modern direct-deposit standard);
    // dropdown still offers Mail / Pickup / Delivered by Supervisor / Other.
    paycheck_distribution_description: 'ACH',
    paycheck_frequency_description: '',
    ssn_last_4: null,
    is_active: true
  });
  // SSN reveal state — never store the plaintext in localStorage / persistent state.
  const [revealedSsn, setRevealedSsn] = useState(null);
  const [revealLoading, setRevealLoading] = useState(false);
  // Draft licenses buffer — only used during new-employee creation. Once the
  // employee is persisted, each entry is POSTed via employeeLicensesAPI.create
  // in handleSave below.
  const [draftLicenses, setDraftLicenses] = useState([]);
  // Same pattern for emergency contacts (multi-record, primary flag enforced).
  const [draftEmergencyContacts, setDraftEmergencyContacts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [clientList, setClientList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [lookupDenied, setLookupDenied] = useState({ locations: false, employees: false });

  useEffect(() => {
    // Load locations + employees regardless of new vs existing. Uses the
    // lightweight directory endpoints so a user without full
    // locations:view / employees:view caps can still populate the
    // supervisor + assignment-location dropdowns gracefully (the dropdowns
    // will degrade with an inline note if the user has neither lookup nor
    // full-module access).
    (async () => {
      const [locR, empR, cliR] = await settle([
        lookupsAPI.locations({ is_active: true }),
        lookupsAPI.employees({ is_active: true }),
        lookupsAPI.clients({ is_active: true }),
      ]);
      setLocations(locR.ok ? (locR.data || []) : []);
      setEmployees(empR.ok ? (empR.data || []).filter(e => e.id !== id) : []);
      setClientList(cliR.ok ? (cliR.data || []) : []);
      setLookupDenied({
        locations: !locR.ok && locR.denied,
        employees: !empR.ok && empR.denied,
      });
    })();
  }, [id]);

  useEffect(() => {
    if (!isNew) {
      loadEmployee();
      loadWallet();
      loadItCheckouts();
      loadEvaluations();
      loadAttachments();
    }
  }, [id]);

  const loadEvaluations = async () => {
    try {
      const r = await evaluationsAPI.list({ employee_id: id });
      setEvaluations(r.data);
    } catch (e) { /* no-op */ }
  };
  const loadAttachments = async () => {
    try {
      const r = await employeeAttachmentsAPI.list(id);
      setAttachments(r.data);
    } catch (e) { /* no-op */ }
  };

  const handleUploadAttachment = async (file) => {
    if (file) {
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append('file', file);
        await employeeAttachmentsAPI.upload(id, fd);
        toast.success('File uploaded');
        loadAttachments();
      } catch (e) {
        toast.error('Upload failed');
      } finally {
        setUploading(false);
      }
    }
  };

  const downloadAttachment = async (att) => {
    try {
      const res = await employeeAttachmentsAPI.download(att.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: att.content_type }));
      const a = document.createElement('a'); a.href = url; a.download = att.filename; a.click();
    } catch (e) { toast.error('Download failed'); }
  };

  const loadEmployee = async () => {
    try {
      const response = await employeesAPI.get(id);
      // Coerce null *string* fields to '' so controlled inputs stay controlled.
      // Booleans + arrays must NOT be coerced — Pydantic rejects '' for List/bool.
      const d = response.data || {};
      const STRING_FIELDS = [
        'employee_number', 'email', 'company', 'position', 'manager_id', 'location_id',
        'assignment_location', 'hire_date', 'post_type', 'state', 'region',
        // New WinTeam-aligned HR / PII fields
        'middle_name', 'address', 'city', 'zip_code',
        'phone_primary', 'phone_secondary',
        'date_of_birth', 'classification_description',
        'paycheck_distribution_description', 'paycheck_frequency_description',
      ];
      const clean = { ...d };
      for (const k of STRING_FIELDS) {
        if (clean[k] == null) clean[k] = '';
      }
      setEmployee(clean);
    } catch (error) {
      toast.error('Failed to load employee');
      navigate('/employees');
    } finally {
      setLoading(false);
    }
  };

  const loadWallet = async () => {
    const response = await walletAPI.get(id).catch(error => { console.error('Failed to load wallet:', error); return null; });
    if (response) setWallet(response.data);
  };

  const loadItCheckouts = async () => {
    const response = await assetCheckoutAPI.list({ employee_id: id, active_only: true }).catch(error => { console.error('Failed to load IT checkouts:', error); return null; });
    if (response) setItCheckouts(response.data);
  };

  const handleSave = async () => {
    if (!employee.first_name || !employee.last_name) {
      toast.error('Please fill in first and last name');
      return;
    }
    // Validate SSN on new-employee create (admin/HR only ever see this input).
    if (isNew && employee.ssn_plaintext) {
      const ssnErr = validateSsn(employee.ssn_plaintext);
      if (ssnErr) {
        toast.error(`SSN: ${ssnErr}`);
        return;
      }
    }

    // Clean empty strings to null for optional fields
    const payload = {
      ...employee,
      employee_number: employee.employee_number || null,
      email: employee.email || null,
      company: employee.company || null,
      position: employee.position || null,
      assignment_location: employee.assignment_location || null,
      location_id: employee.location_id || null,
      hire_date: employee.hire_date || null,
      post_type: employee.post_type || null,
      state: employee.state || null,
      client_ids: employee.client_ids || [],
      // New HR / PII fields
      middle_name: employee.middle_name || null,
      address: employee.address || null,
      city: employee.city || null,
      zip_code: employee.zip_code || null,
      phone_primary: employee.phone_primary || null,
      phone_secondary: employee.phone_secondary || null,
      date_of_birth: employee.date_of_birth || null,
      classification_description: employee.classification_description || null,
      paycheck_distribution_description: employee.paycheck_distribution_description || null,
      paycheck_frequency_description: employee.paycheck_frequency_description || null,
    };
    // SSN is intentionally NOT in this payload — it's set via the dedicated endpoint.
    delete payload.ssn_last_4;
    delete payload.ssn_encrypted;
    delete payload.ssn_plaintext;
    // Strip UI-only flags that track "Other..." text-input visibility.
    delete payload._classification_other_active;
    delete payload._post_type_other_active;
    delete payload._pay_dist_other_active;

    setSaving(true);
    try {
      if (isNew) {
        // v4.38.0 item 6: send SSN on create so the server runs the SSN+DOB
        // duplicate check (and stores it when the caller may hold SSNs).
        const rawSsn = (employee.ssn_plaintext || '').trim();
        const createPayload = rawSsn ? { ...payload, ssn: rawSsn } : payload;
        let created;
        try {
          created = await employeesAPI.create(createPayload);
        } catch (err) {
          const d = err.response?.data?.detail;
          if (err.response?.status === 409 && d && typeof d === 'object' && d.code === 'duplicate_ssn_dob') {
            const ex = d.existing;
            const msg = `${d.message}\n\n` +
              (ex ? `Existing: ${ex.name || ''}${ex.employee_number ? ` (#${ex.employee_number})` : ''}\n\n` : '') +
              `Click OK to add this employee anyway, or Cancel to stop and open the existing record.`;
            if (!window.confirm(msg)) {
              setSaving(false);
              if (ex?.id) navigate(`/employees/${ex.id}`);
              return;
            }
            created = await employeesAPI.create(createPayload, true);  // force
          } else {
            throw err;
          }
        }
        // Fallback for callers who can hold SSNs but create did not store it.
        if (rawSsn && created.data?.id && !created.data?.ssn_last_4) {
          try {
            await employeesAPI.setSsn(created.data.id, rawSsn);
          } catch (ssnErr) {
            toast.error(ssnErr.response?.data?.detail || 'Employee saved but SSN could not be stored — try editing the record.');
          }
        }
        // Flush any draft licenses that the operator queued in the Licensing
        // panel. We POST them sequentially so primary-flag conflicts can't race.
        if (created.data?.id && draftLicenses.length > 0) {
          for (const lic of draftLicenses) {
            try {
              await employeeLicensesAPI.create(created.data.id, lic);
            } catch (licErr) {
              toast.error(`License ${lic.license_number}: ${licErr.response?.data?.detail || 'save failed'}`);
            }
          }
        }
        // Flush any draft emergency contacts in the same fashion.
        if (created.data?.id && draftEmergencyContacts.length > 0) {
          for (const c of draftEmergencyContacts) {
            try {
              await employeeEmergencyContactsAPI.create(created.data.id, c);
            } catch (ecErr) {
              toast.error(`Emergency contact ${c.first_name} ${c.last_name}: ${ecErr.response?.data?.detail || 'save failed'}`);
            }
          }
        }
        toast.success('Employee created successfully');
        navigate('/employees');
      } else {
        await employeesAPI.update(id, payload);
        toast.success('Employee updated successfully');
        navigate('/employees');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save employee');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to permanently delete ${employee.first_name} ${employee.last_name}? This cannot be undone.`)) return;
    try {
      await employeesAPI.delete(id);
      toast.success('Employee deleted');
      navigate('/employees');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to delete employee');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 skeleton-pulse"></div>
        <div className="h-96 skeleton-pulse rounded-lg"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="employee-detail-page">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/employees')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="page-title">
              {isNew ? 'New Employee' : `${employee.first_name} ${employee.last_name}`}
            </h1>
            {!isNew && (
              <p className="text-slate-500 mt-1">Employee #{employee.employee_number}</p>
            )}
          </div>
        </div>
        <div className="flex gap-3 flex-wrap">
          {!isNew && (perms.can('evaluations', 'add') || hasRole('admin', 'hr', 'manager')) && (
            <Button variant="outline" onClick={() => navigate(`/evaluations?new=${id}`)} data-testid="quick-new-evaluation-btn">
              <ClipboardCheck className="w-4 h-4 mr-2" /> New Evaluation
            </Button>
          )}
          {!isNew && (
            <Button variant="outline" onClick={async () => {
              try {
                const res = await employeesAPI.assetCardPdf(id);
                const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                const a = document.createElement('a');
                a.href = url;
                a.download = `asset_card_${employee.first_name}_${employee.last_name}.pdf`;
                a.click();
              } catch { toast.error('Failed to generate asset card'); }
            }} data-testid="print-asset-card-btn">
              <FileText className="w-4 h-4 mr-2" /> Asset Card
            </Button>
          )}
          {!isNew && (
            <RequireCap module="employees" action="delete">
              <Button variant="destructive" onClick={handleDelete} data-testid="delete-employee-btn">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </RequireCap>
          )}
          <RequireCap module="employees" action={isNew ? 'add' : 'edit'}>
            <Button onClick={handleSave} disabled={saving} data-testid="save-employee-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Employee'}
            </Button>
          </RequireCap>
        </div>
      </div>

      {employee?.merged_into && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200" data-testid="merged-record-banner">
          This employee record was <strong>merged</strong> and is now read-only.{' '}
          <button
            type="button"
            className="font-semibold underline"
            data-testid="merged-survivor-link"
            onClick={() => navigate(`/employees/${employee.merged_into}`)}
          >
            Go to the surviving record{employee.merged_into_number ? ` (#${employee.merged_into_number})` : ''}
          </button>.
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6" data-testid="employee-tabs">
        <TabsList>
          <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
          {!isNew && <TabsTrigger value="wallet" data-testid="tab-wallet">Assigned Items ({wallet?.items?.length || 0})</TabsTrigger>}
          {!isNew && <TabsTrigger value="it-assets" data-testid="tab-it-assets">IT Assets ({itCheckouts.length})</TabsTrigger>}
          {!isNew && <TabsTrigger value="evaluations" data-testid="tab-evaluations"><ClipboardCheck className="w-4 h-4 mr-1.5" />Evaluations</TabsTrigger>}
          {/* Phase 4 — Payroll tab (gated by HR payroll grant via access engine).
              Sub-tabs (Compensation, W-4, State Withholding, Banking) ship in Sub-Pass 2. */}
          {!isNew && canSeePayrollTab && (
            <TabsTrigger value="payroll" data-testid="tab-payroll">
              <Banknote className="w-4 h-4 mr-1.5" />Payroll
            </TabsTrigger>
          )}
          {/* Phase 4 — Confidential / HR tab (gated by HR confidential grant).
              Sub-tabs (EEO, I-9, WOTC, Sensitive) ship in Sub-Pass 3. */}
          {!isNew && canSeeConfidentialTab && (
            <TabsTrigger value="confidential" data-testid="tab-confidential">
              <ShieldCheck className="w-4 h-4 mr-1.5" />Confidential
            </TabsTrigger>
          )}
          {!isNew && <TabsTrigger value="attachments" data-testid="tab-attachments"><FolderOpen className="w-4 h-4 mr-1.5" />Attachments</TabsTrigger>}
          {!isNew && canSeeSupervisorLog && <TabsTrigger value="supervisor-log" data-testid="tab-supervisor-log"><ClipboardList className="w-4 h-4 mr-1.5" />Supervisor Log</TabsTrigger>}
          {!isNew && <TabsTrigger value="history" data-testid="tab-history"><History className="w-4 h-4 mr-1.5" />History</TabsTrigger>}
        </TabsList>

        <TabsContent value="details" className="space-y-6">
          {/* Sticky in-page nav — jump straight to a section without scrolling.
              Hidden on the new-employee form since there are fewer sections. */}
          {!isNew && (
            <div className="sticky top-0 z-10 -mx-2 px-2 py-2 bg-surface/90 backdrop-blur border-b border-slate-200" data-testid="employee-subnav">
              <nav className="flex flex-wrap gap-2 text-sm">
                <a href="#personal-information" className="px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center gap-1.5" data-testid="subnav-personal"><UserCircle className="w-4 h-4" /> Personal</a>
                <a href="#position-details" className="px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center gap-1.5" data-testid="subnav-position"><BadgeIcon className="w-4 h-4" /> Position</a>
                <a href="#licensing" className="px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center gap-1.5" data-testid="subnav-licensing"><ClipboardCheck className="w-4 h-4" /> Licensing</a>
                <a href="#emergency-contacts" className="px-3 py-1.5 rounded-full bg-red-50 hover:bg-red-100 text-red-700 inline-flex items-center gap-1.5" data-testid="subnav-emergency"><FolderOpen className="w-4 h-4" /> Emergency Contacts</a>
              </nav>
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Personal Info */}
            <Card id="personal-information" className="lg:col-span-2 scroll-mt-24">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><UserCircle className="w-5 h-5" /> Personal Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="employee_number">Employee Number</Label>
                    <Input
                      id="employee_number"
                      value={employee.employee_number}
                      onChange={(e) => setEmployee({ ...employee, employee_number: e.target.value })}
                      placeholder="EMP001"
                      data-testid="employee-number-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={employee.email || ''}
                      onChange={(e) => setEmployee({ ...employee, email: e.target.value })}
                      placeholder="john.doe@company.com"
                      data-testid="employee-email-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First Name *</Label>
                    <Input
                      id="first_name"
                      value={employee.first_name}
                      onChange={(e) => setEmployee({ ...employee, first_name: e.target.value })}
                      placeholder="John"
                      data-testid="employee-firstname-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="middle_name">Middle Name</Label>
                    <Input
                      id="middle_name"
                      value={employee.middle_name || ''}
                      onChange={(e) => setEmployee({ ...employee, middle_name: e.target.value })}
                      placeholder="Q"
                      data-testid="employee-middle-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last Name *</Label>
                    <Input
                      id="last_name"
                      value={employee.last_name}
                      onChange={(e) => setEmployee({ ...employee, last_name: e.target.value })}
                      placeholder="Doe"
                      data-testid="employee-lastname-input"
                    />
                  </div>
                </div>
                {/* Demographics — Race + Sex (above DOB + SSN per spec) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Race</Label>
                    <Select
                      value={employee.race || ''}
                      onValueChange={(v) => setEmployee({ ...employee, race: v })}
                    >
                      <SelectTrigger data-testid="employee-race"><SelectValue placeholder="Select race" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="American Indian or Alaska Native">American Indian or Alaska Native</SelectItem>
                        <SelectItem value="Asian">Asian</SelectItem>
                        <SelectItem value="Black or African American">Black or African American</SelectItem>
                        <SelectItem value="Hispanic or Latino">Hispanic or Latino</SelectItem>
                        <SelectItem value="Native Hawaiian or Other Pacific Islander">Native Hawaiian or Other Pacific Islander</SelectItem>
                        <SelectItem value="Two or More Races">Two or More Races</SelectItem>
                        <SelectItem value="White">White</SelectItem>
                        <SelectItem value="Prefer Not to Answer">Prefer Not to Answer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Sex</Label>
                    <Select
                      value={employee.sex || ''}
                      onValueChange={(v) => setEmployee({ ...employee, sex: v })}
                    >
                      <SelectTrigger data-testid="employee-sex"><SelectValue placeholder="Select sex" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date_of_birth">Date of Birth</Label>
                    <Input
                      id="date_of_birth"
                      type="date"
                      value={employee.date_of_birth || ''}
                      onChange={(e) => setEmployee({ ...employee, date_of_birth: e.target.value })}
                      data-testid="employee-dob"
                    />
                  </div>
                  <div className="space-y-2">
                    {isNew ? (
                      <>
                        <Label>Social Security Number</Label>
                        {hasRole('admin', 'hr') ? (
                          <>
                            <Input
                              value={employee.ssn_plaintext || ''}
                              onChange={(e) => setEmployee({ ...employee, ssn_plaintext: formatSsn(e.target.value) })}
                              placeholder="123-45-6789"
                              inputMode="numeric"
                              maxLength={11}
                              className={`font-mono ${validateSsn(employee.ssn_plaintext) ? 'border-red-400 focus:ring-red-400' : ''}`}
                              data-testid="employee-ssn-input"
                            />
                            {validateSsn(employee.ssn_plaintext) ? (
                              <p className="text-xs text-red-600" data-testid="employee-ssn-error">{validateSsn(employee.ssn_plaintext)}</p>
                            ) : (
                              <p className="text-xs text-slate-500">Encrypted on save. Admin/HR only. Leave blank to skip.</p>
                            )}
                          </>
                        ) : (
                          <>
                            <Input value="— Admin/HR can add SSN —" readOnly className="font-mono text-slate-400" />
                            <p className="text-xs text-slate-500">SSN entry requires Admin or HR role.</p>
                          </>
                        )}
                      </>
                    ) : (
                      <SensitiveField
                        testId="employee-ssn"
                        label="Social Security Number"
                        module="employees"
                        hrGrant="confidential"
                        redactedValue={employee.ssn_last_4 ? `•••-••-${employee.ssn_last_4}` : null}
                        reveal={async () => {
                          const r = await employeesAPI.revealSsn(id);
                          return r.data?.ssn || null;
                        }}
                        save={async (val) => {
                          await employeesAPI.setSsn(id, val);
                          await loadEmployee();   // refresh ssn_last_4
                        }}
                        clear={async () => {
                          await employeesAPI.clearSsn(id);
                          await loadEmployee();
                        }}
                        format="ssn"
                      />
                    )}
                  </div>
                </div>
                {/* Address + Phones (moved here from Contact & Payroll per UX request) */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="address">Street Address</Label>
                    <Input
                      id="address"
                      value={employee.address || ''}
                      onChange={(e) => setEmployee({ ...employee, address: e.target.value })}
                      placeholder="123 Main St"
                      data-testid="employee-address"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      value={employee.city || ''}
                      onChange={(e) => setEmployee({ ...employee, city: e.target.value })}
                      data-testid="employee-city"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Select value={employee.state || ''} onValueChange={(v) => setEmployee({ ...employee, state: v })}>
                      <SelectTrigger data-testid="employee-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {US_STATES.map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zip_code">ZIP</Label>
                    <Input
                      id="zip_code"
                      value={employee.zip_code || ''}
                      onChange={(e) => setEmployee({ ...employee, zip_code: e.target.value })}
                      data-testid="employee-zip"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone_primary">Phone (Primary)</Label>
                    <div className="flex gap-2">
                      <Select
                        value={employee.phone_primary_type || ''}
                        onValueChange={(v) => setEmployee({ ...employee, phone_primary_type: v })}
                      >
                        <SelectTrigger className="w-28 shrink-0" data-testid="employee-phone-1-type"><SelectValue placeholder="Type" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Home">Home</SelectItem>
                          <SelectItem value="Cell">Cell</SelectItem>
                          <SelectItem value="Office">Office</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        id="phone_primary"
                        value={employee.phone_primary || ''}
                        onChange={(e) => setEmployee({ ...employee, phone_primary: e.target.value })}
                        data-testid="employee-phone-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone_secondary">Phone (Secondary)</Label>
                    <div className="flex gap-2">
                      <Select
                        value={employee.phone_secondary_type || ''}
                        onValueChange={(v) => setEmployee({ ...employee, phone_secondary_type: v })}
                      >
                        <SelectTrigger className="w-28 shrink-0" data-testid="employee-phone-2-type"><SelectValue placeholder="Type" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Home">Home</SelectItem>
                          <SelectItem value="Cell">Cell</SelectItem>
                          <SelectItem value="Office">Office</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        id="phone_secondary"
                        value={employee.phone_secondary || ''}
                        onChange={(e) => setEmployee({ ...employee, phone_secondary: e.target.value })}
                        data-testid="employee-phone-2"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Position Details (renamed from Assignment; now includes Company + Classification) */}
            <Card id="position-details" className="scroll-mt-24">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><BadgeIcon className="w-5 h-5" /> Position Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="hire_date">Hire Date</Label>
                  <Input
                    id="hire_date"
                    type="date"
                    value={employee.hire_date || ''}
                    onChange={(e) => setEmployee({ ...employee, hire_date: e.target.value })}
                    data-testid="employee-hiredate-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Assigned Client Accounts</Label>
                  <SearchableCombobox
                    items={clientList
                      .filter(c => !(employee.client_ids || []).includes(c.id))
                      .map(c => ({
                        _key: c.id,
                        _searchStr: `${c.name} ${c.customer_number || ''} ${c.city || ''}`.toLowerCase(),
                        ...c,
                      }))}
                    value=""
                    onSelect={(v) => v && setEmployee({ ...employee, client_ids: [...(employee.client_ids || []), v] })}
                    placeholder="Add a client this employee is assigned to..."
                    searchPlaceholder="Search client name or number..."
                    emptyText="No clients found"
                    testId="employee-client-picker"
                    renderItem={(c) => (c ? `${c.name}${c.customer_number ? ` (${c.customer_number})` : ''}` : null)}
                  />
                  {(employee.client_ids || []).length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1" data-testid="employee-client-list">
                      {(employee.client_ids || []).map(cid => {
                        const c = clientList.find(x => x.id === cid);
                        return (
                          <button
                            key={cid}
                            type="button"
                            onClick={() => setEmployee({ ...employee, client_ids: (employee.client_ids || []).filter(x => x !== cid) })}
                            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-700 hover:bg-red-50 hover:border-red-200 transition-colors"
                            data-testid={`employee-client-${cid}`}
                          >
                            {c ? c.name : cid}
                            <span className="text-slate-400">×</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <p className="text-xs text-slate-500">
                    The supervisory team on each client gets full evaluation access to this employee, and the
                    signature chain is drawn from those levels. Manage teams under People &amp; Places →
                    Supervisory Groups, then attach them to a client.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Location / Region <span className="text-xs text-slate-400">(inventory &amp; record scoping)</span></Label>
                  <SearchableCombobox
                    items={locations.map(loc => ({ _key: loc.id, _searchStr: `${loc.name} ${loc.code || ''}`.toLowerCase(), ...loc }))}
                    value={employee.location_id || ''}
                    onSelect={(v) => {
                      const loc = locations.find(l => l.id === v);
                      setEmployee({ ...employee, location_id: v || '', assignment_location: loc?.name || '' });
                    }}
                    placeholder="Select a site / account..."
                    searchPlaceholder="Search locations..."
                    emptyText="No locations found"
                    testId="employee-location-select"
                    renderItem={(loc) => loc ? `${loc.name}${loc.code ? ` (${loc.code})` : ''}` : null}
                  />
                  {employee.assignment_location && !employee.location_id && (
                    <p className="text-xs text-amber-600">Legacy free-text value: &quot;{employee.assignment_location}&quot; — please pick a location from the list.</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company">Company</Label>
                  <Select
                    value={employee.company || ''}
                    onValueChange={(value) => setEmployee({ ...employee, company: value })}
                  >
                    <SelectTrigger data-testid="employee-company-select">
                      <SelectValue placeholder="Select company" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Alphabetical, Other at bottom */}
                      <SelectItem value="Cyber">Cyber</SelectItem>
                      <SelectItem value="Exquisite">Exquisite</SelectItem>
                      <SelectItem value="Security">Security</SelectItem>
                      <SelectItem value="Surveillance">Surveillance</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="position">Position <span className="text-xs text-slate-400">(free text — e.g. job title from the onboarding packet)</span></Label>
                  <Input
                    id="position"
                    value={employee.position || ''}
                    onChange={(e) => setEmployee({ ...employee, position: e.target.value })}
                    placeholder="e.g. Clerical/office help"
                    data-testid="employee-position-input"
                  />
                </div>
                {(() => {
                  // Classification dropdown — alphabetical, Other at bottom.
                  // Fix 4 (2026-06-26): tax classifications `W-2` and `1099`
                  // added so the onboarding-packet "Classification: W-2"
                  // lands on a preset rather than free-text.
                  // Use a separate "user explicitly picked Other" flag so legacy
                  // free-text values are silently preserved without surfacing a
                  // stray text input.
                  const presets = ['1099', 'Clerical', 'Executive', 'None', 'Service Worker', 'Supervision', 'W-2'];
                  const current = employee.classification_description || '';
                  const userOther = !!employee._classification_other_active;
                  const showInput = userOther;
                  const displayValue = presets.includes(current) ? current : (userOther ? '__other__' : '');
                  return (
                    <div className="space-y-2">
                      <Label>Classification</Label>
                      <Select
                        value={displayValue}
                        onValueChange={(v) => {
                          if (v === '__other__') {
                            setEmployee({ ...employee, classification_description: '', _classification_other_active: true });
                          } else {
                            setEmployee({ ...employee, classification_description: v, _classification_other_active: false });
                          }
                        }}
                      >
                        <SelectTrigger data-testid="employee-classification-select">
                          <SelectValue placeholder="Select classification" />
                        </SelectTrigger>
                        <SelectContent>
                          {presets.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                          <SelectItem value="__other__">Other…</SelectItem>
                        </SelectContent>
                      </Select>
                      {showInput && (
                        <Input
                          value={current}
                          onChange={(e) => setEmployee({ ...employee, classification_description: e.target.value })}
                          placeholder="Specify classification"
                          data-testid="employee-classification-other"
                        />
                      )}
                    </div>
                  );
                })()}
                {(() => {
                  // Employee Type Description — alphabetical, Other at bottom.
                  // Same pattern as Classification: legacy non-preset values are
                  // preserved silently (dropdown shows placeholder) until the
                  // user picks a new option or explicitly clicks Other.
                  const presets = ['Armed Security Guard', 'Clerical', 'GSOC Operator', 'None', 'Peace Officer', 'Salary', 'Shift Supervisor', 'Special Police Officer', 'Unarmed Security Officer'];
                  const current = employee.post_type || '';
                  const userOther = !!employee._post_type_other_active;
                  const showInput = userOther;
                  const displayValue = presets.includes(current) ? current : (userOther ? '__other__' : '');
                  return (
                    <div className="space-y-2">
                      <Label>Employee Type Description</Label>
                      <Select
                        value={displayValue}
                        onValueChange={(v) => {
                          if (v === '__other__') {
                            setEmployee({ ...employee, post_type: '', _post_type_other_active: true });
                          } else {
                            setEmployee({ ...employee, post_type: v, _post_type_other_active: false });
                          }
                        }}
                      >
                        <SelectTrigger data-testid="employee-post-type"><SelectValue placeholder="Select employee type" /></SelectTrigger>
                        <SelectContent>
                          {presets.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                          <SelectItem value="__other__">Other…</SelectItem>
                        </SelectContent>
                      </Select>
                      {showInput && (
                        <Input
                          value={current}
                          onChange={(e) => setEmployee({ ...employee, post_type: e.target.value })}
                          placeholder="Specify employee type"
                          data-testid="employee-post-type-other"
                        />
                      )}
                    </div>
                  );
                })()}
                <div className="flex items-center justify-between pt-4">
                  <Label htmlFor="is_active">Active Employee</Label>
                  <RequireCap
                    module="employees"
                    action="edit"
                    fallback={
                      <span className="text-xs text-slate-500 inline-flex items-center gap-1.5" data-testid="employee-active-readonly">
                        {employee.is_active ? 'Active' : 'Inactive'} <span className="text-slate-400">(read-only)</span>
                      </span>
                    }
                  >
                    <Switch
                      id="is_active"
                      checked={employee.is_active}
                      onCheckedChange={(checked) => setEmployee({ ...employee, is_active: checked })}
                      data-testid="employee-active-switch"
                    />
                  </RequireCap>
                </div>
              </CardContent>
            </Card>

            {/* Pay Distribution + Pay Frequency moved to the Payroll tab (Phase 4 / Fix 3)
                so they sit alongside Compensation/W-4/State/Banking and inherit the
                payroll-grant gate. They remain on the Employee record (single PUT) and
                are still saved by the global header Save button via the parent form state. */}
              {/* Licensing — multi-record list. On new-employee create the
                  panel runs in draft mode (in-memory buffer); after the
                  employee is persisted the buffered rows are POSTed. */}
              <div id="licensing" className="lg:col-span-3 scroll-mt-24">
                {isNew ? (
                  <LicensingPanel
                    employeeId={null}
                    draftLicenses={draftLicenses}
                    onDraftChange={setDraftLicenses}
                  />
                ) : (
                  id && <LicensingPanel employeeId={id} />
                )}
              </div>
              {/* Firearms Qualification — populated from verified Range Cards (v4.33.3) */}
              {!isNew && id && (
                <div id="firearms-qualification" className="lg:col-span-3 scroll-mt-24">
                  <FirearmsQualificationPanel employeeId={id} />
                </div>
              )}
              {/* Emergency Contacts — same draft/live pattern as Licensing */}
              <div id="emergency-contacts" className="lg:col-span-3 scroll-mt-24">
                {isNew ? (
                  <EmergencyContactsPanel
                    employeeId={null}
                    draftContacts={draftEmergencyContacts}
                    onDraftChange={setDraftEmergencyContacts}
                  />
                ) : (
                  id && <EmergencyContactsPanel employeeId={id} />
                )}
              </div>
          </div>
        </TabsContent>

        {!isNew && (
          <TabsContent value="wallet" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Assigned Items (Wallet)</CardTitle>
                <CardDescription>
                  Items currently assigned to this employee
                </CardDescription>
              </CardHeader>
              <CardContent>
                {wallet?.items?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead>SKU</TableHead>
                        <TableHead>Quantity</TableHead>
                        <TableHead>Condition</TableHead>
                        <TableHead>Issued Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {wallet.items.map(item => (
                        <TableRow key={item.id}>
                          <TableCell className="font-medium">{item.item_name}</TableCell>
                          <TableCell className="font-mono">{item.variant_sku}</TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{item.condition}</Badge>
                          </TableCell>
                          <TableCell>{formatDate(item.issued_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No items currently assigned</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {!isNew && (
          <TabsContent value="it-assets" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>IT Assets Checked Out</CardTitle>
                <CardDescription>
                  IT assets currently assigned to this employee
                </CardDescription>
              </CardHeader>
              <CardContent>
                {itCheckouts.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Asset</TableHead>
                        <TableHead>Asset Tag</TableHead>
                        <TableHead>Serial #</TableHead>
                        <TableHead>Site</TableHead>
                        <TableHead>Checked Out</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {itCheckouts.map(co => (
                        <TableRow key={co.id}>
                          <TableCell className="font-medium">{co.item_name}</TableCell>
                          <TableCell><Badge variant="outline" className="font-mono text-xs">{co.asset_tag || '-'}</Badge></TableCell>
                          <TableCell className="font-mono text-sm">{co.serial_number || '-'}</TableCell>
                          <TableCell>
                            {co.location_name ? (
                              <span className="flex items-center gap-1 text-sm"><MapPin className="w-3 h-3" />{co.location_name}</span>
                            ) : '-'}
                          </TableCell>
                          <TableCell>{formatDate(co.checked_out_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <Laptop className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No IT assets currently checked out</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {!isNew && (
          <TabsContent value="evaluations" className="space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Evaluations</CardTitle>
                  <CardDescription>All performance evaluations for this employee.</CardDescription>
                </div>
                <Button onClick={() => navigate(`/evaluations?new=${id}`)} data-testid="open-evaluations-list">
                  <Plus className="w-4 h-4 mr-2" /> New / View All
                </Button>
              </CardHeader>
              <CardContent>
                {evaluations.length === 0 ? (
                  <div className="text-center py-10 text-slate-500">
                    <ClipboardCheck className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                    <p>No evaluations on file yet</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Period</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Overall</TableHead>
                        <TableHead>Completed</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {evaluations.map(ev => (
                        <TableRow key={ev.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/evaluations/${ev.id}`)}>
                          <TableCell className="text-sm">{ev.period_from} → {ev.period_to}</TableCell>
                          <TableCell><Badge>{ev.status}</Badge></TableCell>
                          <TableCell className="font-mono">{ev.overall_average != null ? ev.overall_average.toFixed(2) : '—'} {ev.overall_band && <span className="text-xs text-slate-500 ml-1">{ev.overall_band}</span>}</TableCell>
                          <TableCell className="text-sm text-slate-500">{ev.completed_at ? formatDate(ev.completed_at) : '—'}</TableCell>
                          <TableCell><Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/evaluations/${ev.id}`); }}>Open</Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {!isNew && (
          <TabsContent value="attachments" className="space-y-6">
            <AttachmentsManager employeeId={id} />
          </TabsContent>
        )}

        {!isNew && canSeeSupervisorLog && (
          <TabsContent value="supervisor-log" className="space-y-6">
            <SupervisorLogPanel employee={employee} />
          </TabsContent>
        )}

        {/* ── Phase 4 / Sub-Pass 2 — Payroll tab (gated by HR payroll grant) ──
            The tab itself only renders for users with the payroll grant
            (see the TabsTrigger above). Bodies for the 4 sub-tabs
            (Compensation, Federal W-4, State Withholding, Banking) ship
            next sub-pass. Stub here proves engine-driven tab visibility
            works end-to-end. */}
        {!isNew && canSeePayrollTab && (
          <TabsContent value="payroll" className="space-y-6" data-testid="payroll-tab-content">
            <PayrollTab employeeId={id} workState={employee?.work_state || employee?.state} employee={employee} setEmployee={setEmployee} />
          </TabsContent>
        )}

        {/* ── Phase 4 / Sub-Pass 3 — Confidential / HR tab (gated by HR confidential grant) ──
            EEO Self-ID, I-9 Work Authorization, WOTC, and Sensitive (LE history,
            military, weapon, religious preference, children) ship next sub-pass.
            Segregated from assignment/scheduling/evaluation by design. */}
        {!isNew && canSeeConfidentialTab && (
          <TabsContent value="confidential" className="space-y-6" data-testid="confidential-tab-content">
            <ConfidentialTab employee={employee} employeeId={id} onSaved={loadEmployee} />
          </TabsContent>
        )}

        {!isNew && (
          <TabsContent value="history" className="space-y-6" data-testid="history-tab-content">
            <EmployeeHistoryTab employeeId={id} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
